import type { AxiosInstance } from 'axios';
import type {
  CreateOrgMemberRequest,
  DeleteOrgMemberResponse,
  OrgMember,
  Organization,
  SubmitVerificationRequest,
  UpdateMemberRoleRequest,
  VerificationRequest,
} from '@platform/auth/org';
import { BILLING_ENDPOINTS as E } from '../endpoints';
import { BILLING_KEY_CHARGE_TIMEOUT_MS } from './timeouts';
import type {
  AddOrgSeatsResult,
  CancelSeatDecreaseRequest,
  CancelSeatDecreaseResult,
  MemberLicenseItem,
  OrgBillingRef,
  OrgBillingSubscription,
  OrgLicenseSummaryItem,
  OrgOutstandingSettlement,
  OrgOwnedPlan,
  OrgPurchaseBlock,
  OrgSeatChangeRequest,
  OrgSeatLicense,
  OrgSeatsPreview,
  OrgSettlementCheckout,
  SaveMemberLicensesPayload,
  SaveMemberLicensesResult,
  ScheduleSeatDecreaseResult,
  SeatSummary,
} from '../types/org-seat.type';
import type {
  ConfirmPaymentRequest,
  ConfirmPaymentResult,
  PaginatedPaymentHistory,
  PaymentHistoryDetail,
  ResumeCheckout,
  SettlementConfirmResult,
} from '../types/payment.type';

/**
 * billing-service 조직 축 호출부. 전부 인증 필요(쿠키 자동 전송).
 * 조직 권한(OrgRole) 검증은 서버가 (x-user-id + orgId) 로 수행한다.
 *
 * ── 왜 팩토리인가 ────────────────────────────────────────────────────────
 * web 과 console-web 은 baseURL·onUnauthorized 가 달라 apiClient 인스턴스를 공유할 수
 * 없다. 그렇다고 호출부를 앱마다 복사하면 300줄짜리 엔드포인트 미러가 두 벌이 된다.
 * 인스턴스만 주입받고 호출부는 여기 한 벌만 둔다.
 *
 * @example
 * // apps/<app>/src/features/business/api/business.api.ts
 * export const businessApi = createBusinessApi(apiClient);
 */
export function createBusinessApi(apiClient: AxiosInstance) {
  return {

    // ── 조직 ──────────────────────────────────────────────
    // 조직 생성 API 는 없다. 서버가 기업회원 확정(국세청 검증 통과) 시 프로비저닝한다.

    myGroups: () =>
      apiClient.get<OrgMember[]>(E.myGroups).then((r) => r.data),

    getGroup: (orgId: string) =>
      apiClient.get<Organization>(E.group(orgId)).then((r) => r.data),

    // ── 멤버 ──────────────────────────────────────────────
    members: (orgId: string) =>
      apiClient.get<OrgMember[]>(E.members(orgId)).then((r) => r.data),

    transferOwnership: (orgId: string, toMemberId: string) =>
      apiClient
        .patch<void>(E.transferOwnership(orgId), { toMemberId })
        .then((r) => r.data),

    updateMemberRole: (
      orgId: string,
      memberId: string,
      body: UpdateMemberRoleRequest,
    ) =>
      apiClient
        .patch<OrgMember>(E.updateMemberRole(orgId, memberId), body)
        .then((r) => r.data),

    suspendMember: (orgId: string, memberId: string) =>
      apiClient
        .patch<OrgMember>(E.suspendMember(orgId, memberId))
        .then((r) => r.data),

    /** 멤버 정지 해제 — SUSPENDED → ACTIVE. */
    reactivateMember: (orgId: string, memberId: string) =>
      apiClient
        .patch<OrgMember>(E.reactivateMember(orgId, memberId))
        .then((r) => r.data),

    /**
     * 구성원 계정 생성(관리자 행위) — 초대가 아니라 계정째로 만든다.
     * 서버가 auth 계정까지 함께 만들고, 그 순간 소속이 확정된다.
     */
    createMember: (orgId: string, body: CreateOrgMemberRequest) =>
      apiClient
        .post<OrgMember>(E.createMember(orgId), body)
        .then((r) => r.data),

    /** 구성원 비밀번호 초기화 — 임시 비밀번호가 본인 메일로 발송되고 세션이 끊긴다. */
    resetMemberPassword: (orgId: string, memberId: string) =>
      apiClient
        .post<{ ok: true }>(E.resetMemberPassword(orgId, memberId))
        .then((r) => r.data),

    /**
     * 구성원 삭제(관리자 행위) — 배정 시트 회수 + org_members 하드 삭제 +
     * auth 계정 잠금·soft-delete. 아이디·이메일·번호는 즉시 재사용할 수 있다.
     */
    deleteMember: (orgId: string, memberId: string) =>
      apiClient
        .delete<DeleteOrgMemberResponse>(E.deleteMember(orgId, memberId))
        .then((r) => r.data),

    // ── 검증 ──────────────────────────────────────────────
    verifications: (orgId: string) =>
      apiClient
        .get<VerificationRequest[]>(E.verifications(orgId))
        .then((r) => r.data),

    submitVerification: (orgId: string, body: SubmitVerificationRequest) =>
      apiClient
        .post<VerificationRequest>(E.verifications(orgId), body)
        .then((r) => r.data),

    // ── 시트(라이선스) ────────────────────────────────────
    seats: (orgId: string) =>
      apiClient.get<SeatSummary>(E.seats(orgId)).then((r) => r.data),

    /** 조직 시트 목록 — 시트 관리 화면(할당/회수). */
    seatLicenses: (orgId: string) =>
      apiClient
        .get<OrgSeatLicense[]>(E.seatLicenses(orgId))
        .then((r) => r.data),

    /** 조직 보유 플랜 목록 — 수량 확장(재구매)용. */
    orgOwnedPlans: (orgId: string) =>
      apiClient
        .get<OrgOwnedPlan[]>(E.seatOwnedPlans(orgId))
        .then((r) => r.data),

    /** 조직이 새로 살 수 없는 플랜과 사유 — 스토어 카드등록 전 확인. PURCHASE_LICENSE 필요. */
    orgPurchaseBlocks: (orgId: string) =>
      apiClient
        .get<OrgPurchaseBlock[]>(E.orgPurchaseBlocks(orgId))
        .then((r) => r.data),

    /** 조직의 해당 플랜 활성 빌링 구독(수량모델) 조회. 없으면 null → 최초 구독(카드등록) 필요. */
    orgBilling: (orgId: string, planId: string) =>
      apiClient
        .get<OrgBillingRef | null>(E.orgBillingSubscription(orgId, planId))
        .then((r) => r.data),

    /** 수량 모델: 기존 빌링 구독에 좌석 추가(잔여기간 proration 즉시 청구). */
    addOrgSeats: (orgId: string, body: OrgSeatChangeRequest) =>
      apiClient
        .post<AddOrgSeatsResult>(E.orgAddSeats(orgId), body, {
          timeout: BILLING_KEY_CHARGE_TIMEOUT_MS,
        })
        .then((r) => r.data),

    /**
     * 좌석 감소 **예약** — 즉시 감소가 아니라 다음 갱신부터 적용된다.
     * 배정 중인 좌석 아래로 내리면 서버가 409 로 막는다(먼저 회수 필요).
     */
    scheduleOrgSeatDecrease: (orgId: string, body: OrgSeatChangeRequest) =>
      apiClient
        .post<ScheduleSeatDecreaseResult>(E.orgDecreaseSeats(orgId), body)
        .then((r) => r.data),

    /** 예약된 좌석 감소 취소. */
    cancelOrgSeatDecrease: (orgId: string, body: CancelSeatDecreaseRequest) =>
      apiClient
        .post<CancelSeatDecreaseResult>(E.orgCancelSeatDecrease(orgId), body)
        .then((r) => r.data),

    /** 좌석 추가 미리보기(dry-run) — 즉시청구/다음갱신 금액. 청구·발급 없음. */
    previewOrgSeats: (
      orgId: string,
      planId: string,
      count: number,
      subscriptionId?: string,
    ) =>
      apiClient
        .get<OrgSeatsPreview>(E.orgSeatsPreview(orgId), {
          params: { planId, count, subscriptionId },
        })
        .then((r) => r.data),

    /** 조직 빌링 구독 목록 — 결제수단(카드) 관리용. VIEW_BILLING. */
    orgBillingSubscriptions: (orgId: string) =>
      apiClient
        .get<OrgBillingSubscription[]>(E.orgBillingSubscriptions(orgId))
        .then((r) => r.data),

    // ── 미수 정산(중도해지 위약금) 셀프 수납 ─────────────────
    /**
     * 조직 미수 정산 목록. VIEW_BILLING.
     * cancelRequestId 를 주면 그 한 건만 — 수납·면제로 종결된 건도 포함해서 돌아온다.
     */
    orgSettlements: (orgId: string, cancelRequestId?: string) =>
      apiClient
        .get<OrgOutstandingSettlement[]>(
          E.orgSettlements(orgId, cancelRequestId),
        )
        .then((r) => r.data),

    /** 결제창 준비 — 금액은 서버가 확정한다. MANAGE_BILLING. */
    orgSettlementCheckout: (orgId: string, cancelRequestId: string) =>
      apiClient
        .post<OrgSettlementCheckout>(
          E.orgSettlementCheckout(orgId, cancelRequestId),
        )
        .then((r) => r.data),

    /** 결제창 성공 리다이렉트 후 승인. MANAGE_BILLING. */
    orgSettlementConfirm: (
      orgId: string,
      cancelRequestId: string,
      payload: ConfirmPaymentRequest,
    ) =>
      apiClient
        .post<SettlementConfirmResult>(
          E.orgSettlementConfirm(orgId, cancelRequestId),
          payload,
          // 승인은 Toss 왕복(15s) + 실패 시 원거래 재확인(5s) + 원장 전이를 기다린다.
          // 게이트웨이 한도(30s)보다 **길어야** 한다 — 화면이 먼저 포기하면 성사된 결제가
          // 실패로 보이고, 고객은 다시 결제한다.
          { timeout: 35_000 },
        )
        .then((r) => r.data),

    /** 라이선스 할당 대상(ACTIVE 멤버) 목록. */
    assignableMembers: (orgId: string) =>
      apiClient
        .get<OrgMember[]>(E.assignableMembers(orgId))
        .then((r) => r.data),

    // ── 제품 단위 라이선스 (§6, 구성원 중심) ──────────────────
    /** 상품별 통합내역(잔여/전체) — 전 구독 합산. */
    licenseSummary: (orgId: string) =>
      apiClient
        .get<OrgLicenseSummaryItem[]>(E.licenseSummary(orgId))
        .then((r) => r.data),

    /** 이 구성원의 상품별 배정 상태. */
    memberLicenses: (orgId: string, userId: string) =>
      apiClient
        .get<MemberLicenseItem[]>(E.memberLicenses(orgId, userId))
        .then((r) => r.data),

    /** 구성원 라이선스 diff 벌크 저장(assign/revoke) — 서버가 단일 TX·전체 롤백. */
    saveMemberLicenses: (
      orgId: string,
      userId: string,
      body: SaveMemberLicensesPayload,
    ) =>
      apiClient
        .post<SaveMemberLicensesResult>(E.memberLicenses(orgId, userId), body)
        .then((r) => r.data),

    // ── 구독 / 결제 ───────────────────────────────────────
    // 구독 생성은 카드등록 후 billing/complete 로만 한다(결제 없는 생성·클라이언트 금액 결제 API 는 없다).
    // 승인은 서버가 Toss 왕복 + 시트 발급을 하므로 시간이 걸릴 수 있으나,
    // 무한 대기(스피너 멈춤)를 막기 위해 상한 타임아웃을 둔다. 초과 시 reject →
    // 화면이 에러 상태로 전환되고, 결제는 멱등(orderId)이라 재결제/재조회로 복구 가능.
    confirmPayment: (body: ConfirmPaymentRequest) =>
      apiClient
        .post<ConfirmPaymentResult>(E.confirmPayment, body, {
          timeout: 35_000,
        })
        .then((r) => r.data),

    // ── 조직 결제내역 (VIEW_BILLING) ──────────────────────
    orgPayments: (orgId: string, page: number, limit: number) =>
      apiClient
        .get<PaginatedPaymentHistory>(E.orgPayments(orgId), {
          params: { page, limit },
        })
        .then((r) => r.data),

    orgPaymentDetail: (orgId: string, id: string) =>
      apiClient
        .get<PaymentHistoryDetail>(E.orgPaymentDetail(orgId, id))
        .then((r) => r.data),

    /** 조직 미완료 결제 재개 → Toss 재open 파라미터. */
    resumeOrgPayment: (orgId: string, id: string) =>
      apiClient
        .post<ResumeCheckout>(E.resumeOrgPayment(orgId, id))
        .then((r) => r.data),
  };
}

export type BusinessApi = ReturnType<typeof createBusinessApi>;
